import { useMemo, useState, useEffect, useRef } from 'react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { useStore } from '../store/useStore';
import { Users, Clock, Activity, CheckCircle, AlertTriangle, Coffee, LifeBuoy, Bell, CheckCheck, User, Reply, MapPin, CalendarDays, Maximize2, Minimize2 } from 'lucide-react';
import { TutorialModal } from './TutorialModal';
import { CalendarExportModal } from './CalendarExportModal';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = (import.meta as any).env.VITE_MAPBOX_ACCESS_TOKEN || '';
mapboxgl.accessToken = MAPBOX_TOKEN;

export function DashboardView() {
  const { team, points, allPoints, shifts, categories, issues, resolveIssue, messages, markMessageRead, markAllMessagesRead, openReply } = useVisibleStore();
  const currentUser = useStore(state => state.currentUser);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showCalendarExport, setShowCalendarExport] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [messagesCollapsed, setMessagesCollapsed] = useState(false);
  const [focusedIssueId, setFocusedIssueId] = useState<string | null>(null);

  // Auto-expand incidents card when coming from notification link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('incident') === 'new') {
      setExpandedCard('incidents');
      // Clean URL after reading
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('incident');
      window.history.replaceState({}, document.title, newUrl.toString());
    }
  }, []);

  const miniMapContainerRef = useRef<HTMLDivElement>(null);
  const miniMapRef = useRef<mapboxgl.Map | null>(null);
  const miniMarkerRef = useRef<mapboxgl.Marker | null>(null);

  const teamOnDuty = team.filter(s => s.isOnDuty && !s.parentArtistId);
  const visibleTeam = team.filter(s => !s.parentArtistId);
  const visibleShifts = shifts;
  
  // Calculate total hours scheduled
  const totalHours = visibleShifts.reduce((acc, shift) => {
    const start = new Date(shift.start).getTime();
    const end = new Date(shift.end).getTime();
    return acc + (end - start) / (1000 * 60 * 60);
  }, 0);

  const visibleIssues = issues
    .sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const unresolvedIssues = useMemo(() => visibleIssues.filter(i => !i.resolved), [visibleIssues]);

  const focusedIssue = useMemo(() => {
    if (!focusedIssueId) return unresolvedIssues[0] || null;
    return unresolvedIssues.find(i => i.id === focusedIssueId) || unresolvedIssues[0] || null;
  }, [focusedIssueId, unresolvedIssues]);

  const focusedPoint = useMemo(() => {
    if (!focusedIssue) return null;
    return (allPoints || points).find(p => p.id === focusedIssue.pointId) || null;
  }, [focusedIssue, allPoints, points]);

  // Mini-map effect
  useEffect(() => {
    if (unresolvedIssues.length === 0 || !miniMapContainerRef.current) {
      if (miniMapRef.current) {
        miniMapRef.current.remove();
        miniMapRef.current = null;
      }
      return;
    }

    if (!miniMapRef.current) {
      // Find dynamic fallback center from visible points
      const fallbackPoint = points.find(p => p.location);
      const fallbackCenter: [number, number] = fallbackPoint 
        ? [fallbackPoint.location!.lng, fallbackPoint.location!.lat] 
        : [2.3522, 48.8566];

      const center: [number, number] = focusedPoint?.location 
        ? [focusedPoint.location.lng, focusedPoint.location.lat]
        : fallbackCenter;

      const map = new mapboxgl.Map({
        container: miniMapContainerRef.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: center,
        zoom: 15,
        pitch: 45,
        attributionControl: false
      });

      miniMapRef.current = map;
    }

    const map = miniMapRef.current;

    // Clean old marker
    if (miniMarkerRef.current) {
      miniMarkerRef.current.remove();
      miniMarkerRef.current = null;
    }

    if (focusedPoint?.location) {
      const { lat, lng } = focusedPoint.location;
      
      map.flyTo({
        center: [lng, lat],
        zoom: 17,
        speed: 1.2,
        essential: true
      });

      const el = document.createElement('div');
      el.className = 'relative flex h-8 w-8 items-center justify-center cursor-pointer';
      el.innerHTML = `
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
        <span class="relative inline-flex rounded-full h-4 w-4 bg-red-600 border-2 border-white shadow-lg"></span>
      `;

      const popup = new mapboxgl.Popup({ offset: 15, closeButton: false })
        .setHTML(`
          <div style="color: #ffffff; font-family: sans-serif; padding: 4px; font-size: 11px; line-height: 1.4;">
            <strong style="display: block; font-size: 12px; margin-bottom: 2px; color: #ffffff;">${focusedPoint.name}</strong>
            <p style="margin: 0; color: #94a3b8;">${focusedIssue?.teamName || 'Team'} : ${focusedIssue?.message || ''}</p>
          </div>
        `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map);

      miniMarkerRef.current = marker;
    }
  }, [focusedPoint, focusedIssue, unresolvedIssues, points]);

  // Clean map on unmount
  useEffect(() => {
    return () => {
      if (miniMapRef.current) {
        miniMapRef.current.remove();
        miniMapRef.current = null;
      }
    };
  }, []);

    // Calculation of missed meals based on actual meal points opening hours
    const missedMeals = useMemo(() => {
      const alerts: { teamId: string; teamName: string; mealName: string; window: string; locationHint: string; dayLabel: string; sortDate: number }[] = [];

      // Find points that are meal points (either explicitly marked or by name)
      const mealPoints = (allPoints || points).filter(p => 
        p.isMealPoint || 
        p.name.toLowerCase().includes('déjeuner') || 
        p.name.toLowerCase().includes('dejeuner') || 
        p.name.toLowerCase().includes('diner') || 
        p.name.toLowerCase().includes('dîner')
      );

      if (mealPoints.length === 0) return alerts;

      visibleTeam.forEach(member => {
        if (member.isArtist) return; // Artists don't get meal alerts
        const memberShifts = visibleShifts.filter(s => s.teamId === member.id);
        if (memberShifts.length === 0) return;

        // Group team shifts by day (YYYY-MM-DD)
        const shiftsByDay = new Map<string, typeof memberShifts>();
        memberShifts.forEach(s => {
           const date = new Date(s.start);
           const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
           if (!shiftsByDay.has(dayKey)) shiftsByDay.set(dayKey, []);
           shiftsByDay.get(dayKey)!.push(s);
        });

        shiftsByDay.forEach((dayShifts, dayKey) => {
          const [year, month, day] = dayKey.split('-').map(Number);
          const dayDate = new Date(year, month - 1, day);
          const dayLabel = dayDate.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

          mealPoints.forEach(mealPoint => {
            // Find opening hours of this meal point on this specific day
            const dayOpeningHours = mealPoint.openingHours.filter(slot => {
              const startSlot = new Date(slot.start);
              return startSlot.getFullYear() === year && startSlot.getMonth() === month - 1 && startSlot.getDate() === day;
            });

            dayOpeningHours.forEach(slot => {
              const windowStart = new Date(slot.start);
              const windowEnd = new Date(slot.end);

              // Check if the team has any shifts overlapping this meal window
              // If they have no shifts at all overlapping the window, they are free and can eat.
              const shiftsInWindow = dayShifts.filter(s => new Date(s.start) < windowEnd && new Date(s.end) > windowStart);
              if (shiftsInWindow.length === 0) return; // Free time, no missed meal!

              // Shifts that block them from eating (not at a meal point)
              const blockingShifts = shiftsInWindow.filter(s => {
                const pt = (allPoints || points).find(p => p.id === s.pointId);
                return !(pt?.isMealPoint || pt?.name.toLowerCase().includes('déjeuner') || pt?.name.toLowerCase().includes('dejeuner') || pt?.name.toLowerCase().includes('diner') || pt?.name.toLowerCase().includes('dîner'));
              });

              let timeline = [{ start: windowStart, end: windowEnd }];
              
              blockingShifts.forEach(s => {
                const sStart = new Date(s.start);
                const sEnd = new Date(s.end);
                let newTimeline: { start: Date, end: Date }[] = [];
                for (const block of timeline) {
                   if (sEnd <= block.start || sStart >= block.end) {
                     newTimeline.push(block);
                   } else if (sStart <= block.start && sEnd >= block.end) {
                     // Covered completely
                   } else if (sStart > block.start && sEnd < block.end) {
                     newTimeline.push({ start: block.start, end: sStart });
                     newTimeline.push({ start: sEnd, end: block.end });
                   } else if (sStart <= block.start) {
                     newTimeline.push({ start: sEnd, end: block.end });
                   } else if (sEnd >= block.end) {
                     newTimeline.push({ start: block.start, end: sStart });
                   }
                }
                timeline = newTimeline;
              });

              let maxGapMins = 0;
              timeline.forEach(block => {
                 const mins = (block.end.getTime() - block.start.getTime()) / 60000;
                 if (mins > maxGapMins) maxGapMins = mins;
              });

              if (maxGapMins < 30) {
                let longestOverlap = 0;
                let hint = 'Inconnu';
                blockingShifts.forEach(s => {
                   const sStart = new Date(s.start);
                   const sEnd = new Date(s.end);
                   const overlapStart = sStart < windowStart ? windowStart : sStart;
                   const overlapEnd = sEnd > windowEnd ? windowEnd : sEnd;
                   const overlap = overlapEnd.getTime() - overlapStart.getTime();
                   if (overlap > longestOverlap) {
                     longestOverlap = overlap;
                     const pt = (allPoints || points).find(p => p.id === s.pointId);
                     hint = pt?.name || 'Inconnu';
                   }
                });

                alerts.push({
                  teamId: member.id,
                  teamName: member.name,
                  mealName: mealPoint.name,
                  window: `${windowStart.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${windowEnd.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
                  locationHint: hint,
                  dayLabel,
                  sortDate: windowStart.getTime()
                });
              }
            });
          });
        });
      });

      return alerts.sort((a, b) => a.sortDate - b.sortDate);
    }, [visibleTeam, visibleShifts, points]);

  // Generate CSV Export for Shifts
  const handleExportCSV = () => {
    const headers = ['Team Name', 'Category', 'Role(s)', 'Point Name', 'Start Time (Planned)', 'End Time (Planned)', 'Actual Start', 'Actual End', 'Duration (Hours)', 'Issues Reported'];
    
    const rows = visibleShifts.map(shift => {
      const assignedTeam = team.find(s => s.id === shift.teamId);
      const assignedPoint = (allPoints || points).find(p => p.id === shift.pointId);
      const cat = categories.find(c => c.id === assignedTeam?.categoryId);
      
      const start = new Date(shift.start);
      const end = new Date(shift.end);
      const duration = ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toFixed(2);
      
      const shiftIssuesCount = issues.filter(i => i.teamId === shift.teamId && new Date(i.timestamp) >= new Date(shift.start) && new Date(i.timestamp) <= new Date(shift.end)).length;

      return [
        assignedTeam?.name || 'Unknown',
        cat?.name || 'General',
        (assignedTeam?.roles || [assignedTeam?.role]).join(', '),
        assignedPoint?.name || 'Unknown',
        shift.start.replace('T', ' '),
        shift.end.replace('T', ' '),
        shift.actualStart ? shift.actualStart.replace('T', ' ') : 'Not Checked In',
        shift.actualEnd ? shift.actualEnd.replace('T', ' ') : 'Not Checked Out',
        duration,
        shiftIssuesCount.toString()
      ].map(val => `"${val}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `planning_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-3.5 sm:p-6 md:p-8 h-full overflow-y-auto overflow-x-hidden relative">
      {/* Background glow */}
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6 sm:mb-8 relative z-10">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Tableau de bord</h2>
          <p className="text-slate-400 text-xs sm:text-sm">Vue d'ensemble de la planification et état des équipes en temps réel.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <button 
            onClick={() => setShowTutorial(true)}
            className="flex-1 sm:flex-initial bg-[#1A1D23] hover:bg-[#2D3139] text-teal-400 border border-[#2D3139] hover:border-teal-500/30 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2"
          >
            <LifeBuoy className="w-4 h-4" />
            <span>Tutoriel</span>
          </button>
          <button 
            onClick={() => setShowCalendarExport(true)}
            className="flex-1 sm:flex-initial bg-[#1A1D23] hover:bg-[#2D3139] text-violet-400 border border-[#2D3139] hover:border-violet-500/30 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2"
          >
            <CalendarDays className="w-4 h-4" />
            <span>Export Calendrier</span>
          </button>
          <button 
            onClick={handleExportCSV}
            className="flex-1 sm:flex-initial bg-[#1A1D23] hover:bg-[#2D3139] text-indigo-400 border border-[#2D3139] hover:border-indigo-500/30 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            <span>CSV</span>
          </button>
        </div>
      </div>

      {/* ─── TEAM MESSAGES (top) ───────────────────────────────── */}
      {(() => {
        const myEmail = currentUser.email || localStorage.getItem('wfm_user_email');
        const myTeam = myEmail ? team.find(s => s.email?.toLowerCase().trim() === myEmail.toLowerCase().trim()) : null;
        const myTeamId = myTeam?.id;
        const visibleTeamIds = new Set(team.map(s => s.id));
        // Only show messages for this admin (or broadcasts with no specific recipient)
        const teamMessages = (messages || [])
          .filter(m => m.fromTeam && visibleTeamIds.has(m.teamId) && (!m.recipientTeamId || m.recipientTeamId === myTeamId))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        const unreadCount = teamMessages.filter(m => !m.read).length;
        if (teamMessages.length === 0) return null;
        return (
          <div className="relative z-10 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
              <h2 className="font-bold text-white text-sm sm:text-base flex items-center gap-2">
                <Bell className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-indigo-400" />
                Messages du Team
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2 justify-between sm:justify-end w-full sm:w-auto">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllMessagesRead}
                    className="flex items-center gap-1 text-[10px] sm:text-xs font-bold text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 px-2.5 py-1.5 rounded-lg transition-colors"
                  >
                    <CheckCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Tout marquer lu
                  </button>
                )}
                <button
                  onClick={() => setMessagesCollapsed(c => !c)}
                  className="text-[10px] sm:text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded transition-colors ml-auto sm:ml-0"
                >
                  {messagesCollapsed ? '▼ Afficher' : '▲ Réduire'}
                </button>
              </div>
            </div>
            {!messagesCollapsed && (
              <div className="space-y-2 max-h-72 overflow-y-auto custom-scrollbar pr-1">
                {teamMessages.map(m => {
                  const sender = team.find(s => s.id === m.teamId);
                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col sm:flex-row sm:items-start gap-3 p-3.5 rounded-xl border transition-all ${
                        m.read
                          ? 'bg-[#14161B]/60 border-[#2D3139]/50 opacity-60'
                          : 'bg-emerald-500/5 border-emerald-500/20'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 w-full sm:w-auto">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden border border-[#2D3139] flex items-center justify-center bg-[#1A1D23]">
                          {sender?.photo ? <img src={sender.photo} alt={sender.name} className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-slate-400" />}
                        </div>
                        <div className="min-w-0 sm:hidden flex-1">
                          <span className="text-xs font-semibold text-white truncate block">{sender?.name || m.senderName || 'Team'}</span>
                          <span className="text-[9px] text-slate-500 block">{new Date(m.timestamp).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="hidden sm:flex items-baseline justify-between gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-white truncate">{sender?.name || m.senderName || 'Team'}</span>
                          <span className="text-[10px] text-slate-500">{new Date(m.timestamp).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">{m.content}</p>
                      </div>
                      
                      <div className="flex flex-row sm:flex-col items-center sm:items-end justify-end sm:justify-start gap-2 border-t sm:border-t-0 border-[#2D3139]/30 pt-2 sm:pt-0 mt-1 sm:mt-0 w-full sm:w-auto">
                        {!m.read ? (
                          <button onClick={() => markMessageRead(m.id)} className="flex-shrink-0 flex items-center gap-1 text-[9px] sm:text-[10px] font-bold text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 px-2 py-1 rounded-lg transition-colors whitespace-nowrap">
                            <CheckCheck className="w-3 h-3" /> Lu ✓
                          </button>
                        ) : (
                          <span className="flex-shrink-0 text-[9px] sm:text-[10px] text-slate-600 flex items-center gap-1"><CheckCheck className="w-3 h-3" /> Lu</span>
                        )}
                        <button
                          onClick={() => openReply(m.teamId)}
                          className="flex items-center gap-1 text-[9px] sm:text-[10px] font-bold text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 px-2 py-1 rounded-lg transition-colors whitespace-nowrap"
                        >
                          <Reply className="w-3 h-3" /> Répondre
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-10 relative z-10">
        <div className="bg-[#14161B] border border-[#2D3139] hover:border-indigo-500/50 p-4 sm:p-6 rounded-xl flex items-center space-x-3 sm:space-x-5 transition-all duration-300 group">
          <div className="w-10 h-10 sm:w-14 sm:h-14 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center border border-indigo-500/20 group-hover:scale-105 transition-transform">
            <Users className="w-5 h-5 sm:w-7 sm:h-7" />
          </div>
          <div className="min-w-0">
            <p className="text-slate-400 text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">Total Team</p>
            <p className="text-xl sm:text-3xl font-bold text-white tracking-tight">{visibleTeam.length}</p>
          </div>
        </div>

        <div className="bg-[#14161B] border border-[#2D3139] hover:border-emerald-500/50 p-4 sm:p-6 rounded-xl flex items-center space-x-3 sm:space-x-5 transition-all duration-300 group">
          <div className="w-10 h-10 sm:w-14 sm:h-14 bg-emerald-500/10 text-emerald-400 rounded-xl flex items-center justify-center border border-emerald-500/20 group-hover:scale-105 transition-transform">
            <Activity className="w-5 h-5 sm:w-7 sm:h-7" />
          </div>
          <div className="min-w-0">
            <p className="text-slate-400 text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">En Service</p>
            <p className="text-xl sm:text-3xl font-bold text-white tracking-tight">{teamOnDuty.length}</p>
          </div>
        </div>

        <div className="bg-[#14161B] border border-[#2D3139] hover:border-amber-500/50 p-4 sm:p-6 rounded-xl flex items-center space-x-3 sm:space-x-5 transition-all duration-300 group">
          <div className="w-10 h-10 sm:w-14 sm:h-14 bg-amber-500/10 text-amber-400 rounded-xl flex items-center justify-center border border-amber-500/20 group-hover:scale-105 transition-transform">
            <AlertTriangle className="w-5 h-5 sm:w-7 sm:h-7" />
          </div>
          <div className="min-w-0">
            <p className="text-slate-400 text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">Alertes</p>
            <p className={`text-xl sm:text-3xl font-bold tracking-tight ${unresolvedIssues.length > 0 ? 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.3)]' : 'text-white'}`}>{unresolvedIssues.length}</p>
          </div>
        </div>

        <div className="bg-[#14161B] border border-[#2D3139] hover:border-blue-500/50 p-4 sm:p-6 rounded-xl flex items-center space-x-3 sm:space-x-5 transition-all duration-300 group">
          <div className="w-10 h-10 sm:w-14 sm:h-14 bg-blue-500/10 text-blue-400 rounded-xl flex items-center justify-center border border-blue-500/20 group-hover:scale-105 transition-transform">
            <Clock className="w-5 h-5 sm:w-7 sm:h-7" />
          </div>
          <div className="min-w-0">
            <p className="text-slate-400 text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">Heures Plan</p>
            <p className="text-xl sm:text-3xl font-bold text-white tracking-tight">{Math.round(totalHours)}h</p>
          </div>
        </div>
      </div>

      <style>{`
        .mapboxgl-popup-content {
          background: #14161B !important;
          color: white !important;
          border: 1px solid #2D3139 !important;
          border-radius: 12px !important;
          padding: 8px !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5) !important;
          max-width: 220px !important;
        }
        .mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
          border-bottom-color: #2D3139 !important;
        }
        .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
          border-top-color: #2D3139 !important;
        }
        .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
          border-right-color: #2D3139 !important;
        }
        .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
          border-left-color: #2D3139 !important;
        }
        .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib {
          display: none !important;
        }
      `}</style>

      <div className="flex flex-col gap-5 relative z-10 w-full">

        {/* Urgent Issues */}
        <div className={`bg-[#14161B] border border-[#2D3139] rounded-xl overflow-hidden flex flex-col shadow-sm ${expandedCard === 'incidents' ? 'fixed left-0 md:left-[280px] right-0 top-0 bottom-0 z-[250] m-4' : 'h-[520px]'}`}>
          <div className="p-4 sm:p-5 border-b border-[#2D3139]/50 bg-[#1A1D23] flex justify-between items-center">
            <h3 className="text-sm sm:text-base font-bold text-white flex items-center">
              <span className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center mr-2.5 border border-amber-500/20">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              </span>
              Incidents Signalés
            </h3>
            <button onClick={() => setExpandedCard(expandedCard === 'incidents' ? null : 'incidents')} className="text-slate-400 hover:text-white">
              {expandedCard === 'incidents' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
          
          {unresolvedIssues.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 space-y-4 flex-1">
              <AlertTriangle className="w-12 h-12 opacity-20" />
              <p className="text-sm">Aucun problème signalé.</p>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row flex-1 min-h-0">
              {/* List of Incidents (left) */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3 border-r border-[#2D3139]/40">
                {unresolvedIssues.map(issue => {
                  const pt = (allPoints || points).find(p => p.id === issue.pointId);
                  const isInternal = issue.type === 'incident_internal';
                  return (
                    <div 
                      key={issue.id} 
                      onClick={() => setFocusedIssueId(issue.id)}
                      className={`border p-4 rounded-lg flex flex-col transition-all cursor-pointer ${
                        focusedIssueId === issue.id || (!focusedIssueId && unresolvedIssues[0]?.id === issue.id)
                          ? 'border-indigo-500 bg-[#1A1D23]/60'
                          : isInternal 
                            ? 'bg-rose-950/10 hover:bg-rose-950/20 border-rose-500/20' 
                            : 'bg-red-950/15 hover:bg-red-950/25 border-red-500/25'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-bold text-xs tracking-wide ${isInternal ? 'text-rose-400' : 'text-red-400'}`}>
                              {issue.teamName}
                            </p>
                            <span className={`text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded-full ${
                              isInternal 
                                ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' 
                                : 'bg-red-500/10 text-red-400 border border-red-500/20'
                            }`}>
                              {isInternal ? '🔒 Interne' : '🌐 Public'}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1 font-semibold">
                            Lieu : <span className="text-slate-300">{pt?.name || 'Point inconnu'}</span>
                          </p>
                        </div>
                        <span className="text-[9px] text-slate-500 font-medium">{new Date(issue.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed bg-[#0A0B0E]/60 p-2.5 rounded border border-[#2D3139]/40 truncate">{issue.message}</p>
                    </div>
                  );
                })}
              </div>

              {/* Map & Details Pane (right) */}
              <div className="w-full md:w-[300px] lg:w-[360px] flex flex-col border-t md:border-t-0 border-[#2D3139]/40 bg-[#0A0B0E]/40">
                {/* Mini Map */}
                <div className="h-[200px] w-full relative border-b border-[#2D3139]/40 bg-[#0A0B0E]">
                  <div ref={miniMapContainerRef} className="w-full h-full" />
                  
                  {!focusedPoint?.location && (
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-[1px] flex items-center justify-center p-4 text-center z-10">
                      <p className="text-[11px] text-slate-400 font-medium flex flex-col items-center gap-1">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <span>Aucune coordonnée GPS configurée</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Details / Action area */}
                <div className="p-4 flex-1 overflow-y-auto custom-scrollbar flex flex-col justify-between gap-3">
                  {focusedIssue ? (
                    <div className="space-y-3 flex-1 flex flex-col justify-between font-sans">
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Détails de l'incident</span>
                          <span className="text-[9px] text-slate-500 font-semibold">{new Date(focusedIssue.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                            Signalé par {focusedIssue.teamName}
                          </h4>
                          <p className="text-[10px] text-slate-400 mt-1">Lieu : <strong className="text-white">{focusedPoint?.name || 'Inconnu'}</strong></p>
                        </div>
                        <div className="p-3 bg-[#0A0B0E]/80 border border-[#2D3139]/60 rounded-lg max-h-[110px] overflow-y-auto custom-scrollbar">
                          <p className="text-xs text-slate-200 leading-relaxed break-words whitespace-pre-line">{focusedIssue.message}</p>
                        </div>
                      </div>

                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          resolveIssue(focusedIssue.id);
                        }}
                        className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-xs transition-colors uppercase tracking-wider mt-2"
                      >
                        Marquer Résolu
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500 text-xs py-8">
                      <AlertTriangle className="w-8 h-8 opacity-20 mb-2" />
                      Sélectionnez un incident
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          </div>  {/* closes Incident card (line 500) */}

          {/* ─── Team En Service ─── */}
          <div className="bg-[#14161B] border border-[#2D3139] rounded-xl shadow-sm overflow-hidden flex-shrink-0">            <div className="px-4 py-3 flex items-center justify-between bg-[#1A1D23] border-b border-[#2D3139]/50">
              <div className="flex items-center gap-2.5">                <span className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /></span>
                <h3 className="text-sm font-bold text-white">Team En Service</h3>
                {teamOnDuty.length > 0 && <span className="bg-emerald-500/10 text-emerald-400 text-[10px] px-2 py-0.5 rounded-full font-bold border border-emerald-500/20">{teamOnDuty.length}</span>}
              </div>
              <button onClick={() => setExpandedCard(expandedCard === 'team' ? null : 'team')} className="p-1.5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white flex-shrink-0">
                {expandedCard === 'team' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
            <div className={expandedCard === 'team' ? 'p-4 max-h-[70vh] overflow-y-auto custom-scrollbar' : 'p-4 max-h-[300px] overflow-y-auto custom-scrollbar'}>
              {teamOnDuty.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-500 gap-2"><CheckCircle className="w-8 h-8 opacity-20" /><p className="text-xs">Aucun team en
  service.</p></div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {teamOnDuty.map(s => (
                    <div key={s.id} className="flex items-center gap-3 p-2.5 bg-[#0A0B0E]/60 border border-[#2D3139]/80 rounded-lg">
                      {s.photo ? <img src={s.photo} className="w-8 h-8 rounded-full object-cover border border-[#2D3139] flex-shrink-0" /> : <div className="w-8 h-8 rounded-full bg-emerald-500/10 border  border-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-xs flex-shrink-0">{s.name[0]}</div>}
                      <div className="flex-1 min-w-0"><p className="text-white font-bold text-xs truncate">{s.name}</p><p className="text-slate-400 text-[9px] truncate">{(s.roles || [s.role]).join(', ')}</p></div>
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ─── Alertes Repas ─── */}
          <div className="bg-[#14161B] border border-[#2D3139] rounded-xl shadow-sm overflow-hidden flex-shrink-0">
            <div className="px-4 py-3 flex items-center justify-between bg-[#1A1D23] border-b border-[#2D3139]/50">
              <div className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-lg bg-fuchsia-500/10 flex items-center justify-center border border-fuchsia-500/20"><Coffee className="w-3.5 h-3.5 text-fuchsia-400" /></span>
                <h3 className="text-sm font-bold text-white">Alertes Repas</h3>
                {missedMeals.length > 0 && <span className="bg-fuchsia-500/10 text-fuchsia-400 text-[10px] px-2 py-0.5 rounded-full font-bold border border-fuchsia-500/20">{missedMeals.length}</span>}
              </div>
              <div className="flex items-center gap-2">
                {missedMeals.length > 0 && (
                  <button onClick={() => { const csv = 'Nom,Repas,Créneau,Jour\n' + missedMeals.map(m => `"${m.teamName}","${m.mealName}","${m.window}","${m.dayLabel}"`).join('\n'); const b = new
  Blob(['' + csv], { type: 'text/csv;charset=utf-8;' }); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'alertes_repas.csv'; a.click();
  URL.revokeObjectURL(u); }} className="text-[9px] font-bold text-fuchsia-400 hover:text-fuchsia-300 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 px-2 py-0.5 rounded-lg border border-fuchsia-500/20">📥
  Export</button>
                )}
                <button onClick={() => setExpandedCard(expandedCard === 'meals' ? null : 'meals')} className="p-1.5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white flex-shrink-0">
                  {expandedCard === 'meals' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className={expandedCard === 'meals' ? 'p-4 max-h-[70vh] overflow-y-auto custom-scrollbar' : 'p-4 max-h-[300px] overflow-y-auto custom-scrollbar'}>
              {missedMeals.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-500 gap-2"><Coffee className="w-8 h-8 opacity-20" /><p className="text-xs">Aucun repas manqué prévu.</p></div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    const byDay = new Map<string, typeof missedMeals>();
                    missedMeals.forEach(m => {
                      const d = m.dayLabel;
                      if (!byDay.has(d)) byDay.set(d, []);
                      byDay.get(d)!.push(m);
                    });
                    return [...byDay.entries()].map(([day, meals]) => (
                      <div key={day}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">{day}</span>
                          <span className="text-[9px] text-slate-500">{meals.length} repas manqués</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {meals.map((meal, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-[#0A0B0E]/60 border border-[#2D3139]/80 rounded-lg">
                              <span className="text-white font-bold text-xs flex-1 truncate">{meal.teamName}</span>
                              <span className="text-fuchsia-400 text-[9px] font-bold bg-fuchsia-500/10 px-1.5 py-0.5 rounded border border-fuchsia-500/20 whitespace-nowrap">{meal.mealName} ({meal.window})</span>
                              <span className="text-slate-400 text-[9px] truncate">📍 {meal.locationHint}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

        </div>  {/* closes the flex-col gap-5 cards container (line 497) */}

      <TutorialModal 
        isOpen={showTutorial} 
        onClose={() => setShowTutorial(false)} 
      />

      {/* Calendar Export Modal */}
      <CalendarExportModal
        isOpen={showCalendarExport}
        onClose={() => setShowCalendarExport(false)}
      />
    </div>
  );
}
